import { useMemo, useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { formatSinceTime } from '@helpers';
import { useFetchDevicesFiltered } from '@hooks/useFetchDevicesFiltered';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';

export interface UseManageDevicesParams {
  rootStack?: SettingsRootStack;
}

export const useManageDevices = ({ rootStack = 'Settings' }: UseManageDevicesParams = {}) => {
  const navigation = useNavigation();
  const { devices, loaded, isDeviceMore } = useFetchDevicesFiltered({ orderBy: { LastUsedOn: 'Desc' } });

  const formattedDevices = useMemo(
    () =>
      devices.map((d) => ({
        ...d,
        lastUsedOnFormatted: d.lastUsedOn ? formatSinceTime(d.lastUsedOn) : '',
        registeredOnFrom: d.registeredOnFrom ? formatSinceTime(d.registeredOnFrom) : '',
      })),
    [devices],
  );

  const navigateToDeviceDetails = useCallback(
    (deviceId: string) => {
      navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Devices', 'DeviceDetails', { id: deviceId }) as never);
    },
    [navigation, rootStack],
  );

  const navigateToAddDevice = useCallback(() => {
    navigation.navigate('AddDevice' as never);
  }, [navigation]);

  return {
    devices: formattedDevices,
    loaded,
    isDeviceMore,
    navigateToDeviceDetails,
    navigateToAddDevice,
  };
};
